import { useMemo } from 'react'
import { Button as HeroButton } from '@heroui/react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
}

export function Pagination({ currentPage, totalItems, itemsPerPage, onPageChange }: PaginationProps) {
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  const pageNumbers = useMemo(() => {
    const pages: number[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)
      if (start > 2) pages.push(-1) // ellipsis marker
      for (let i = start; i <= end; i++) pages.push(i)
      if (end < totalPages - 1) pages.push(-2) // ellipsis marker
      pages.push(totalPages)
    }
    return pages
  }, [currentPage, totalPages])

  if (totalPages <= 1) return null

  return (
    <div className="flex items-center justify-between pt-4">
      <span className="text-xs text-default-500">
        Showing {startItem}-{endItem} of {totalItems} entries
      </span>
      <div className="flex items-center gap-1">
        <HeroButton
          isIconOnly
          size="sm"
          variant="flat"
          isDisabled={currentPage <= 1}
          onPress={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </HeroButton>
        {pageNumbers.map((page, idx) =>
          page < 0 ? (
            <span key={`ellipsis-${idx}`} className="px-1 text-default-400">...</span>
          ) : (
            <HeroButton
              key={page}
              size="sm"
              variant={page === currentPage ? 'solid' : 'flat'}
              color={page === currentPage ? 'primary' : 'default'}
              onPress={() => onPageChange(page)}
            >
              {page}
            </HeroButton>
          )
        )}
        <HeroButton
          isIconOnly
          size="sm"
          variant="flat"
          isDisabled={currentPage >= totalPages}
          onPress={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </HeroButton>
      </div>
    </div>
  )
}
